import { useMemo } from "react";

import type { ActivationAlertsContractSocialMediaChannel } from "@similarweb/pro-api-client";
import { Combobox } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { ChannelIcon } from "../../common/ChannelIcon";
import { useMultiselectFilterDisplayText } from "../../filters/hooks/useMultiselectFilterDisplayText";
import { type SimpleMultiselectFilterProps, SimpleMultiselectFilter } from "../../filters/SimpleMultiselectFilter";

import { ALL_CHANNELS, useSocialAlertsFilterStore } from "./useSocialAlertsFilterStore";

const renderOption: SimpleMultiselectFilterProps<ActivationAlertsContractSocialMediaChannel>["renderOption"] = (
    item,
    isSelected,
) => (
    <Flex
        alignItems="center"
        justifyContent="space-between"
        gap="8px"
        width="100%"
    >
        <Flex
            alignItems="center"
            gap="8px"
        >
            <ChannelIcon channel={item.id} />
            <Combobox.OptionLabel>{item.label}</Combobox.OptionLabel>
        </Flex>
        <Combobox.OptionCheckbox isChecked={isSelected} />
    </Flex>
);

export const SocialChannel = () => {
    const selectedChannels = useSocialAlertsFilterStore((state) => state.selectedChannels);
    const isDefaultChannelsSelected = useSocialAlertsFilterStore((state) => state.isDefaultChannelsSelected);
    const setSelectedChannels = useSocialAlertsFilterStore((state) => state.setSelectedChannels);

    const options = useMemo(() => ALL_CHANNELS.map((channel) => ({ id: channel, label: channel })), []);

    const displayText = useMultiselectFilterDisplayText({
        defaultSelectionKey: "activation.alerts.filters.social-channel.all",
        isDefaultSelected: isDefaultChannelsSelected,
        nonDefaultSelectionKey: "activation.alerts.filters.social-channel.some",
        selectedFilterData: selectedChannels,
    });

    return (
        <SimpleMultiselectFilter<ActivationAlertsContractSocialMediaChannel>
            appliedItems={selectedChannels}
            dataAutomation="social-channel-filter"
            isFilterSelected={!isDefaultChannelsSelected}
            onApplyChanges={setSelectedChannels}
            options={options}
            renderOption={renderOption}
        >
            {displayText}
        </SimpleMultiselectFilter>
    );
};
